import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-error-warning-line text-4xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-800 mb-2">
              文章未找到
            </h1>
            <Link
              to="/"
              className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: article.category },
    { label: article.title },
  ];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '好色tv',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <Navbar />
      <main className="pt-16">
        <article>
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="mb-6">
              <span className="inline-block px-3 py-1 bg-accent-500/10 text-accent-700 text-xs font-medium rounded-full mb-3">
                {article.category}
              </span>
              <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-3 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line"></i>
                  </div>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line"></i>
                  </div>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </div>
                  {article.views.toLocaleString()} 阅读
                </span>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-lg aspect-video mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-invert max-w-none
                prose-headings:text-foreground-950 prose-headings:font-heading
                prose-h2:text-lg md:prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:text-sm md:prose-p:text-base prose-p:leading-relaxed prose-p:mb-4
                prose-strong:text-foreground-900
                prose-a:text-primary-500"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200/50">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 bg-secondary-100 text-secondary-800 text-xs rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100/50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-video mb-2.5">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{ra.date}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}